import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import StickyBox from 'react-sticky-box';
import Breadcrumb from '../Breadcrumb';
import Sidebar from '../Sidebar';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Api } from '../../auth/Configs';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import {
  ARCHIVE_PROFILES,
  GED_DOCUMENT_EVENTS,
  GED_DOCUMENT_SEARCH_ADVANCED,
  PLAN_FOLDERS,
  PLAN_META_LIST,
  REGLES_CONSERVATION,
} from '../../auth/urlParameters';
import { ToastError } from '../../../ui/AlertesUi';
import { SimpleSpinner } from '../../../ui/Spinner';
import './ArchiveStyles.css';
import { getAssignedServiceIds } from './serviceScope';

const flattenServices = (nodes, depth = 0) =>
  (nodes || []).reduce((acc, node) => {
    acc.push({
      id: node.id,
      label: `${'-- '.repeat(depth)}${node.code} - ${node.title}`,
    });
    if (node.children && node.children.length > 0) {
      acc.push(...flattenServices(node.children, depth + 1));
    }
    return acc;
  }, []);

const flattenFolders = (nodes, depth = 0) =>
  (nodes || []).reduce((acc, node) => {
    acc.push({
      id: node.id,
      planId: node.planId || null,
      serviceUnit: node.serviceUnit || null,
      label: `${'-- '.repeat(depth)}${node.reference} - ${node.title}`,
    });
    if (node.children && node.children.length > 0) {
      acc.push(...flattenFolders(node.children, depth + 1));
    }
    return acc;
  }, []);

const sortPlans = (a, b) => {
  const codeA = (a.code || '').toString();
  const codeB = (b.code || '').toString();
  if (codeA && codeB && codeA !== codeB) {
    return codeA.localeCompare(codeB);
  }
  return (a.title || '').toString().localeCompare((b.title || '').toString());
};

const buildPlanTree = (plans) => {
  const byId = new Map();
  (plans || []).forEach((plan) => {
    byId.set(Number(plan.id), { ...plan, children: [] });
  });
  const roots = [];
  byId.forEach((node) => {
    const parentId = node.parentId ? Number(node.parentId) : null;
    if (parentId && byId.has(parentId)) {
      byId.get(parentId).children.push(node);
    } else {
      roots.push(node);
    }
  });
  const sortNode = (node) => {
    node.children.sort(sortPlans);
    node.children.forEach(sortNode);
  };
  roots.sort(sortPlans);
  roots.forEach(sortNode);
  return roots;
};

const flattenPlanTree = (nodes, depth = 0) =>
  (nodes || []).reduce((acc, node) => {
    acc.push({ plan: node, depth });
    if (node.children && node.children.length > 0) {
      acc.push(...flattenPlanTree(node.children, depth + 1));
    }
    return acc;
  }, []);

const createRule = () => ({
  id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
  field: 'title',
  operator: 'contains',
  value: '',
  key: '',
});

const statusOptions = [
  { value: 'draft', label: 'Brouillon' },
  { value: 'En revue', label: 'En revue' },
  { value: 'À corriger', label: 'À corriger' },
  { value: 'Actif Conservée', label: 'Validée' },
];

const conservationOptions = [
  { value: 'pending', label: 'Pas encore en conservation' },
  { value: 'in_conservation', label: 'En conservation' },
  { value: 'transferred', label: 'Transférée' },
  { value: 'destroyed', label: 'Détruite' },
];

const fieldOptions = [
  { value: 'title', label: 'Titre', type: 'text' },
  { value: 'reference', label: 'Reference', type: 'text' },
  { value: 'status', label: 'Validation', type: 'select' },
  { value: 'conservationStatus', label: 'Conservation', type: 'select' },
  { value: 'plan', label: 'Plan', type: 'select' },
  { value: 'folder', label: 'Dossier', type: 'select' },
  { value: 'profile', label: 'Profil', type: 'select' },
  { value: 'service', label: 'Service', type: 'select' },
  { value: 'regle', label: 'Regle', type: 'select' },
  { value: 'confidentiality', label: 'Confidentialite', type: 'text' },
  { value: 'createdAt', label: 'Date creation', type: 'date' },
  { value: 'retentionEndAt', label: 'Fin conservation', type: 'date' },
  { value: 'metadata', label: 'Metadonnees (cle)', type: 'metadata' },
];

const operatorOptions = [
  { value: 'contains', label: 'Contient' },
  { value: 'equals', label: 'Egal' },
  { value: 'startsWith', label: 'Commence par' },
  { value: 'endsWith', label: 'Finit par' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '>=' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '<=' },
  { value: 'in', label: 'Dans' },
  { value: 'isEmpty', label: 'Est vide' },
  { value: 'notEmpty', label: 'Non vide' },
];

const noValueOperators = new Set(['isEmpty', 'notEmpty']);

const eventTypeLabels = {
  created: 'Création',
  status_change: 'Changement de statut',
  version_added: 'Nouvelle version',
  metadata_update: 'Mise à jour des métadonnées',
  metadata_proposed: 'Correction de métadonnées proposée',
  metadata_rejected: 'Correction de métadonnées refusée',
  metadata_reviewed: 'Métadonnées vérifiées',
  updated: 'Modification',
  consultation: 'Consultation',
  trash: 'Mise en corbeille',
  delete: 'Suppression définitive',
  restore: 'Restauration',
};

const SearchArchiveItems = () => {
  const session = useSelector((state) => state.sessionAuth);
  const profile = useSelector((state) => state.userSettings);
  const navigate = useNavigate();
  const location = useLocation();
  const autoSearchKey = useRef(null);

  const [plans, setPlans] = useState([]);
  const [folders, setFolders] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [regles, setRegles] = useState([]);
  const [services, setServices] = useState([]);

  const [filters, setFilters] = useState({
    planId: '',
    folderId: '',
    profileId: '',
    regleId: '',
    serviceId: '',
    status: '',
    conservationStatus: '',
    retentionDue: '',
    confidentialityMax: '',
    dateFrom: '',
    dateTo: '',
    hasFile: '',
  });
  const [query, setQuery] = useState('');
  const [matchMode, setMatchMode] = useState('all');
  const [rules, setRules] = useState([createRule()]);
  const [sortBy, setSortBy] = useState('date_desc');
  const [results, setResults] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [eventsModalOpen, setEventsModalOpen] = useState(false);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState('');
  const [eventsDocument, setEventsDocument] = useState(null);
  const [eventsList, setEventsList] = useState([]);

  const roles = Array.isArray(session.roles) ? session.roles : [];
  const isAssistantOnly =
    roles.includes('ROLE_ASSIST')
    && !roles.some((role) =>
      ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_EDITED'].includes(role)
    );
  const isReaderOnly =
    roles.includes('ROLE_USER')
    && !roles.some((role) =>
      ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_EDITED', 'ROLE_ASSIST'].includes(role)
    );
  const applyServiceScope = useMemo(
    () => isAssistantOnly || isReaderOnly,
    [isAssistantOnly, isReaderOnly]
  );

  const accessFilters = profile?.accessFilters || {};
  const planScopeIds = useMemo(
    () => (Array.isArray(accessFilters.planIds) ? accessFilters.planIds.map(Number) : []),
    [accessFilters.planIds]
  );
  const folderScopeIds = useMemo(
    () => (Array.isArray(accessFilters.folderIds) ? accessFilters.folderIds.map(Number) : []),
    [accessFilters.folderIds]
  );
  const serviceScopeIds = useMemo(() => getAssignedServiceIds(profile), [profile]);
  const hasPlanScope = planScopeIds.length > 0;
  const hasFolderScope = folderScopeIds.length > 0;
  const hasServiceData = useMemo(
    () => folders.some((folder) => folder?.serviceUnit?.id),
    [folders]
  );

  const authHeaders = useMemo(
    () => ({
      headers: { Authorization: 'Bearer ' + session.token },
    }),
    [session.token]
  );

  const matchesPlanScope = (planId) =>
    !hasPlanScope || planScopeIds.includes(Number(planId));

  const matchesServiceScope = (folder) => {
    if (!applyServiceScope || !serviceScopeIds.length || !hasServiceData) return true;
    const service = folder.serviceUnit;
    if (!service || !service.id) return false;
    return serviceScopeIds.includes(Number(service.id));
  };

  const matchesFolderScope = (folderId) =>
    !hasFolderScope || folderScopeIds.includes(Number(folderId));

  const scopedFolders = useMemo(
    () =>
      folders.filter(
        (folder) =>
          matchesPlanScope(folder.planId) &&
          matchesServiceScope(folder) &&
          matchesFolderScope(folder.id)
      ),
    [folders, hasPlanScope, planScopeIds, hasFolderScope, folderScopeIds, serviceScopeIds, applyServiceScope]
  );

  const availablePlans = useMemo(() => {
    let list = plans;
    if (hasPlanScope) {
      list = list.filter((plan) => planScopeIds.includes(Number(plan.id)));
    }
    if ((applyServiceScope && serviceScopeIds.length) || hasFolderScope) {
      const allowedPlanIds = new Set(scopedFolders.map((folder) => Number(folder.planId)));
      if (allowedPlanIds.size > 0) {
        list = list.filter((plan) => allowedPlanIds.has(Number(plan.id)));
      } else {
        list = [];
      }
    }
    return list;
  }, [
    plans,
    scopedFolders,
    hasPlanScope,
    planScopeIds,
    hasFolderScope,
    serviceScopeIds,
    applyServiceScope,
  ]);

  const groupedPlanOptions = useMemo(
    () => flattenPlanTree(buildPlanTree(availablePlans)),
    [availablePlans]
  );

  const filteredFolders = useMemo(() => {
    if (!filters.planId) return [];
    const planId = Number(filters.planId);
    return scopedFolders.filter((folder) => Number(folder.planId) === planId);
  }, [scopedFolders, filters.planId]);

  useEffect(() => {
    axios
      .get(PLAN_META_LIST(), authHeaders)
      .then((res) => {
        setPlans(res.data?.plans || []);
      })
      .catch(() => setPlans([]));
  }, [authHeaders]);

  useEffect(() => {
    axios
      .get(PLAN_FOLDERS(), authHeaders)
      .then((res) => {
        const flat = flattenFolders(res.data?.folders || []);
        setFolders(flat);
      })
      .catch(() => setFolders([]));
  }, [authHeaders]);

  useEffect(() => {
    axios
      .get(ARCHIVE_PROFILES(), {
        ...authHeaders,
        params: { status: 'active', available: 1, limit: 100 },
      })
      .then((res) => {
        setProfiles(res.data?.profiles || []);
      })
      .catch(() => setProfiles([]));
  }, [authHeaders]);

  useEffect(() => {
    axios
      .get(REGLES_CONSERVATION(), authHeaders)
      .then((res) => {
        setRegles(res.data?.regles || []);
      })
      .catch(() => setRegles([]));
  }, [authHeaders]);

  useEffect(() => {
    axios
      .get(`${Api}/services`, authHeaders)
      .then((res) => {
        const flat = flattenServices(res.data?.services || []);
        setServices(flat);
      })
      .catch(() => setServices([]));
  }, [authHeaders]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const planId = params.get('planId');
    const folderId = params.get('folderId');
    const urlQuery = params.get('q');
    if (urlQuery !== null) {
      setQuery(urlQuery);
    }
    if (planId) {
      setFilters((prev) => ({ ...prev, planId }));
    }
    if (folderId) {
      setFilters((prev) => ({ ...prev, folderId }));
    }
  }, [location.search]);

  useEffect(() => {
    if (!filters.planId && filters.folderId) {
      setFilters((prev) => ({ ...prev, folderId: '' }));
    }
  }, [filters.planId, filters.folderId]);

  const updateFilter = (key, value) => {
    if (key === 'planId') {
      setFilters((prev) => ({ ...prev, planId: value, folderId: '' }));
      return;
    }
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const updateRule = (id, updates) => {
    setRules((prev) => prev.map((rule) => (rule.id === id ? { ...rule, ...updates } : rule)));
  };

  const addRule = () => {
    setRules((prev) => [...prev, createRule()]);
  };

  const removeRule = (id) => {
    setRules((prev) => prev.filter((rule) => rule.id !== id));
  };

  const toIdArray = (value) => (value ? [Number(value)] : []);
  const toStringArray = (value) => (value ? [value] : []);
  const shouldIncludeRule = (rule) => {
    if (!rule.field) return false;
    const operator = rule.operator || 'contains';
    if (noValueOperators.has(operator)) {
      if (rule.field === 'metadata') {
        return rule.key && String(rule.key).trim() !== '';
      }
      return true;
    }
    if (rule.field === 'metadata' && (!rule.key || String(rule.key).trim() === '')) {
      return false;
    }
    if (rule.value === null || rule.value === undefined) return false;
    if (Array.isArray(rule.value)) return rule.value.length > 0;
    return String(rule.value).trim() !== '';
  };

  const toNullableBool = (v) => v === '' ? null : v === 'true' ? true : v === 'false' ? false : null;

  const formatEventType = (type) => eventTypeLabels[type] || type || '—';

  const formatEventAuthor = (event) => {
    const author = event?.performedBy;
    if (!author) return '—';
    return author.name || author.username || '—';
  };

  const openEventsModal = async (doc) => {
    if (!doc?.id) {
      ToastError('Identifiant introuvable pour ce document.');
      return;
    }
    setEventsModalOpen(true);
    setEventsLoading(true);
    setEventsError('');
    try {
      const res = await axios.get(GED_DOCUMENT_EVENTS(doc.id), authHeaders);
      setEventsDocument(res.data?.document || doc);
      setEventsList(res.data?.events || []);
    } catch (err) {
      setEventsError(err.response?.data?.message || 'Impossible de charger les événements.');
      setEventsList([]);
    } finally {
      setEventsLoading(false);
    }
  };

  const closeEventsModal = () => {
    setEventsModalOpen(false);
    setEventsDocument(null);
    setEventsList([]);
    setEventsError('');
  };

  const buildPayload = useCallback((pageValue = page, perPageValue = perPage) => ({
    q: query,
    match: matchMode,
    rules: rules
      .filter(shouldIncludeRule)
      .map((rule) => ({
        field: rule.field,
        operator: rule.operator,
        value: noValueOperators.has(rule.operator) ? null : rule.value,
        key: rule.field === 'metadata' ? rule.key : undefined,
      })),
    filters: {
      planIds: toIdArray(filters.planId),
      folderIds: toIdArray(filters.folderId),
      profileIds: toIdArray(filters.profileId),
      regleIds: toIdArray(filters.regleId),
      serviceIds: toIdArray(filters.serviceId),
      status: toStringArray(filters.status),
      conservationStatus: toStringArray(filters.conservationStatus),
      retentionDue: toNullableBool(filters.retentionDue),
      confidentialityMax: filters.confidentialityMax,
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      hasFile: toNullableBool(filters.hasFile),
    },
    sort: sortBy,
    page: pageValue,
    perPage: perPageValue,
  }), [query, matchMode, rules, filters, sortBy, page, perPage]);

  const runSearch = useCallback((pageValue = page, perPageValue = perPage, queryOverride = query) => {
    setLoading(true);
    setPage(pageValue);
    setPerPage(perPageValue);
    axios
      .post(GED_DOCUMENT_SEARCH_ADVANCED(), {
        ...buildPayload(pageValue, perPageValue),
        q: queryOverride,
      }, authHeaders)
      .then((res) => {
        setResults(res.data?.documents || []);
        setCount(res.data?.count || 0);
      })
      .catch(() => {
        ToastError('Impossible de lancer la recherche avancee.');
        setResults([]);
        setCount(0);
      })
      .finally(() => setLoading(false));
  }, [page, perPage, query, buildPayload, authHeaders]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlQuery = params.get('q')?.trim();
    if (params.get('auto') !== '1' || !urlQuery || autoSearchKey.current === location.key) return;
    autoSearchKey.current = location.key;
    runSearch(1, perPage, urlQuery);
  }, [location.key, location.search, perPage, runSearch]);

  const handleSearch = () => {
    runSearch(1, perPage);
  };

  const handleReset = () => {
    setQuery('');
    setMatchMode('all');
    setRules([createRule()]);
    setFilters({
      planId: '',
      folderId: '',
      profileId: '',
      regleId: '',
      serviceId: '',
      status: '',
      conservationStatus: '',
      retentionDue: '',
      confidentialityMax: '',
      dateFrom: '',
      dateTo: '',
      hasFile: '',
    });
    setResults([]);
    setCount(0);
    setPage(1);
    setPerPage(50);
  };

  const renderValueInput = (rule, fieldType, options) => {
    if (noValueOperators.has(rule.operator)) {
      return <span className="text-muted">—</span>;
    }
    if (fieldType === 'select') {
      return (
        <select
          className="form-control form-control-sm"
          value={rule.value || ''}
          onChange={(e) => updateRule(rule.id, { value: e.target.value })}
        >
          <option value="">Choisir</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    }
    if (fieldType === 'date') {
      return (
        <input
          type="date"
          className="form-control form-control-sm"
          value={rule.value || ''}
          onChange={(e) => updateRule(rule.id, { value: e.target.value })}
        />
      );
    }
    return (
      <input
        type="text"
        className="form-control form-control-sm"
        value={rule.value || ''}
        onChange={(e) => updateRule(rule.id, { value: e.target.value })}
      />
    );
  };

  const canView = roles.some((role) =>
    ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_EDITED', 'ROLE_ASSIST', 'ROLE_USER'].includes(role)
  );
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(count / perPage)),
    [count, perPage]
  );
  const pageStart = count === 0 ? 0 : (page - 1) * perPage + 1;
  const pageEnd = Math.min(page * perPage, count);

  return (
    <Fragment>
      <Breadcrumb title="Archives / Recherche avancee" />
      <div className="content archive-search-page">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-5 col-lg-4 col-xl-3 theiaStickySidebar">
              <StickyBox offsetTop={20} offsetBottom={20}>
                <Sidebar />
              </StickyBox>
            </div>

            <div className="col-md-7 col-lg-8 col-xl-9">
              <div className="archive-page-header">
                <div>
                  <h4 className="htitle">Recherche avancee</h4>
                  <p className="text-muted mb-0">Combinez texte libre et filtres.</p>
                </div>
                <button className="btn btn-light btn-uniform-action" type="button" onClick={() => navigate('/apps/in/archives')}>
                  Retour aux archives
                </button>
              </div>

              <div className="archive-content search-advanced-card">
                <div className="search-panel">
                  <div className="search-panel-header">
                    <h5 className="mb-0">Recherche</h5>
                    <i className="ri-arrow-down-s-line"></i>
                  </div>
                  <div className="search-panel-body">
                    <div className="row">
                      <div className="col-md-6 col-lg-6">
                        <div className="form-group">
                          <label>Texte integral</label>
                          <input
                            className="form-control"
                            placeholder="Mot-cle, reference, profil..."
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-md-3 col-lg-3">
                        <div className="form-group">
                          <label>Mode</label>
                          <select
                            className="form-control"
                            value={matchMode}
                            onChange={(e) => setMatchMode(e.target.value)}
                          >
                            <option value="all">Toutes les regles</option>
                            <option value="any">Au moins une</option>
                          </select>
                        </div>
                      </div>
                      <div className="col-md-3 col-lg-3">
                        <div className="form-group">
                          <label>Tri</label>
                          <select
                            className="form-control"
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                          >
                            <option value="date_desc">Date (recent)</option>
                            <option value="date_asc">Date (ancien)</option>
                            <option value="title">Titre</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="search-filters-grid">
                      <div className="row">
                        <div className="col-md-6 col-lg-4">
                          <div className="form-group">
                            <label>Plan</label>
                            <select
                              className="form-control"
                              value={filters.planId}
                              onChange={(e) => updateFilter('planId', e.target.value)}
                            >
                              <option value="">Tous</option>
                              {groupedPlanOptions.map(({ plan, depth }) => {
                                const prefix = depth > 0 ? '-- '.repeat(depth) : '';
                                const label = `${prefix}${plan.code} - ${plan.title}`;
                                return (
                                  <option key={plan.id} value={plan.id}>
                                    {label}
                                  </option>
                                );
                              })}
                            </select>
                          </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                          <div className="form-group">
                            <label>Dossier</label>
                            <select
                              className="form-control"
                              disabled={!filters.planId}
                              value={filters.folderId}
                              onChange={(e) => updateFilter('folderId', e.target.value)}
                            >
                              <option value="">{filters.planId ? 'Tous' : 'Choisir un plan'}</option>
                              {filteredFolders.map((folder) => (
                                <option key={folder.id} value={folder.id}>
                                  {folder.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                          <div className="form-group">
                            <label>Profil d'archive</label>
                            <select
                              className="form-control"
                              value={filters.profileId}
                              onChange={(e) => updateFilter('profileId', e.target.value)}
                            >
                              <option value="">Tous</option>
                              {profiles.map((item) => (
                                <option key={item.id} value={item.id}>
                                  {item.code} - {item.title}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                          <div className="form-group">
                            <label>Regle de conservation</label>
                            <select
                              className="form-control"
                              value={filters.regleId}
                              onChange={(e) => updateFilter('regleId', e.target.value)}
                            >
                              <option value="">Tous</option>
                              {regles.map((item) => (
                                <option key={item.id} value={item.id}>
                                  {item.regle}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                          <div className="form-group">
                            <label>Service producteur</label>
                            <select
                              className="form-control"
                              disabled={isAssistantOnly}
                              value={filters.serviceId}
                              onChange={(e) => updateFilter('serviceId', e.target.value)}
                            >
                              <option value="">Tous</option>
                              {services.map((item) => (
                                <option key={item.id} value={item.id}>
                                  {item.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                          <div className="form-group">
                            <label>Validation</label>
                            <select
                              className="form-control"
                              value={filters.status}
                              onChange={(e) => updateFilter('status', e.target.value)}
                            >
                              <option value="">Tous</option>
                              {statusOptions.map((item) => (
                                <option key={item.value} value={item.value}>
                                  {item.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                          <div className="form-group">
                            <label>Conservation</label>
                            <select className="form-control" value={filters.conservationStatus} onChange={(e) => updateFilter('conservationStatus', e.target.value)}>
                              <option value="">Toutes</option>
                              {conservationOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                            </select>
                          </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                          <div className="form-group">
                            <label>Échéance</label>
                            <select className="form-control" value={filters.retentionDue} onChange={(e) => updateFilter('retentionDue', e.target.value)}>
                              <option value="">Toutes</option>
                              <option value="true">À traiter</option>
                              <option value="false">Non échue</option>
                            </select>
                          </div>
                        </div>

                        <div className="col-md-6 col-lg-4">
                          <div className="form-group">
                            <label>Date creation</label>
                            <div className="search-filter-range">
                              <input
                                type="date"
                                className="form-control"
                                value={filters.dateFrom}
                                onChange={(e) => updateFilter('dateFrom', e.target.value)}
                              />
                              <input
                                type="date"
                                className="form-control"
                                value={filters.dateTo}
                                onChange={(e) => updateFilter('dateTo', e.target.value)}
                              />
                            </div>
                          </div>
                        </div>
                      
                      </div>
                    </div>

                    <div className="search-actions">
                      <button className="btn btn-primary btn-uniform-action" type="button" onClick={handleSearch}>
                        Rechercher
                      </button>
                      <button className="btn btn-outline-secondary btn-uniform-action" type="button" onClick={handleReset}>
                        Réinitialiser
                      </button>
                    </div>
                  </div>
                </div>

                <div className="search-rules-panel">
                  <div className="table-responsive">
                    <table className="table table-sm syidocs-data-table">
                      <thead>
                        <tr>
                          <th>Champ</th>
                          <th>Operateur</th>
                          <th>Valeur</th>
                          <th className="syidocs-data-table-actions">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rules.map((rule) => {
                          const field = fieldOptions.find((opt) => opt.value === rule.field);
                          const fieldType = field?.type || 'text';
                          let options = [];
                          if (fieldType === 'select') {
                            if (rule.field === 'status') {
                              options = statusOptions;
                            } else if (rule.field === 'conservationStatus') {
                              options = conservationOptions;
                            } else if (rule.field === 'plan') {
                              options = groupedPlanOptions.map(({ plan, depth }) => {
                                const prefix = depth > 0 ? '-- '.repeat(depth) : '';
                                return {
                                  value: plan.id,
                                  label: `${prefix}${plan.code} - ${plan.title}`,
                                };
                              });
                            } else if (rule.field === 'folder') {
                              options = scopedFolders.map((folder) => ({
                                value: folder.id,
                                label: folder.label,
                              }));
                            } else if (rule.field === 'profile') {
                              options = profiles.map((item) => ({
                                value: item.id,
                                label: `${item.code} - ${item.title}`,
                              }));
                            } else if (rule.field === 'service') {
                              options = services.map((item) => ({
                                value: item.id,
                                label: item.label,
                              }));
                            } else if (rule.field === 'regle') {
                              options = regles.map((item) => ({
                                value: item.id,
                                label: item.regle,
                              }));
                            }
                          }
                          return (
                            <tr key={rule.id}>
                              <td>
                                <select
                                  className="form-control form-control-sm"
                                  value={rule.field}
                                  onChange={(e) => updateRule(rule.id, { field: e.target.value, value: '', key: '' })}
                                >
                                  {fieldOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                                {fieldType === 'metadata' && (
                                  <input
                                    className="form-control form-control-sm mt-1"
                                    placeholder="cle metadonnee"
                                    value={rule.key || ''}
                                    onChange={(e) => updateRule(rule.id, { key: e.target.value })}
                                  />
                                )}
                              </td>
                              <td>
                                <select
                                  className="form-control form-control-sm"
                                  value={rule.operator}
                                  onChange={(e) => updateRule(rule.id, { operator: e.target.value })}
                                >
                                  {operatorOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td>{renderValueInput(rule, fieldType, options)}</td>
                              <td>
                                <button
                                  className="syidocs-table-icon-button is-danger"
                                  type="button"
                                  aria-label="Supprimer ce critère"
                                  title="Supprimer ce critère"
                                  onClick={() => removeRule(rule.id)}
                                  disabled={rules.length === 1}
                                >
                                  <i className="ri-close-line" aria-hidden="true" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="search-rules-actions">
                    <p className="search-rules-hint">
                      <i className="ri-information-line" aria-hidden="true" />
                      Ajoutez vos règles, renseignez leurs valeurs, puis relancez la recherche pour actualiser les résultats.
                    </p>
                    <button className="btn btn-outline-primary btn-sm btn-uniform-action" type="button" onClick={addRule}>
                      + Ajouter une règle
                    </button>
                  </div>
                </div>
              </div>

              <div className="archive-list mt-4">
                <div className="archive-list-header">
                  <div className="archive-count">
                    <span>{count} archives</span>
                  </div>
                  <div className="archive-list-actions">
                    <button className="btn btn-primary btn-sm" type="button" onClick={handleSearch}>
                      Relancer la recherche
                    </button>
                  </div>
                </div>
                {loading ? (
                  <div className="archive-loading">{SimpleSpinner()}</div>
                ) : results.length === 0 ? (
                  <div className="archive-empty">Aucun resultat.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover syidocs-data-table">
                      <thead>
                        <tr>
                          <th>Reference</th>
                          <th>Titre</th>
                          <th>Profil</th>
                          <th>Validation</th>
                          <th>Conservation</th>
                          <th>Date</th>
                          <th className="syidocs-data-table-actions">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {results.map((doc) => (
                          <tr key={doc.id}>
                            <td>{doc.reference || '—'}</td>
                            <td>{doc.title}</td>
                            <td>{doc.archiveProfile?.title || '—'}</td>
                            <td>
                              {statusOptions.find((item) => item.value === (doc.validationStatus || doc.status))?.label || doc.validationStatus || '—'}
                              {['En corbeille', 'Détruite'].includes(doc.status) && <span className="archive-search-technical">{doc.status === 'Détruite' ? 'Supprimée' : doc.status}</span>}
                            </td>
                            <td>
                              {conservationOptions.find((item) => item.value === doc.conservationStatus)?.label || '—'}
                              {doc.retentionDue && <span className="archive-search-due">Échéance à traiter</span>}
                            </td>
                            <td>{doc.date || '—'}</td>
                            <td>
                              {canView && (
                                doc.status === 'Détruite' ? (
                                  <button
                                    className="syidocs-table-icon-button"
                                    type="button"
                                    aria-label={`Afficher les détails de ${doc.title}`}
                                    title="Détails"
                                    onClick={() => openEventsModal(doc)}
                                  >
                                    <i className="ri-eye-line" aria-hidden="true" />
                                  </button>
                                ) : (
                                  <button
                                    className="syidocs-table-icon-button"
                                    type="button"
                                    aria-label={`Voir ${doc.title}`}
                                    title="Voir"
                                    onClick={() => {
                                      if (!doc.uuid) {
                                        ToastError('UUID introuvable pour ce document.');
                                        return;
                                      }
                                      navigate(`/apps/in/archives/detail/${doc.uuid}`);
                                    }}
                                  >
                                    <i className="ri-eye-line" aria-hidden="true" />
                                  </button>
                                )
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {!loading && count > 0 && (
                  <div className="archive-pagination">
                    <div className="archive-pagination-info">
                      Affichage {pageStart}-{pageEnd} sur {count}
                    </div>
                    <div className="archive-pagination-controls">
                      <button
                        className="btn btn-light btn-sm"
                        type="button"
                        onClick={() => runSearch(Math.max(1, page - 1), perPage)}
                        disabled={page <= 1}
                      >
                        Précédent
                      </button>
                      <span className="archive-pagination-page">
                        Page {page} / {totalPages}
                      </span>
                      <button
                        className="btn btn-light btn-sm"
                        type="button"
                        onClick={() => runSearch(Math.min(totalPages, page + 1), perPage)}
                        disabled={page >= totalPages}
                      >
                        Suivant
                      </button>
                      <select
                        className="form-select form-select-sm"
                        value={perPage}
                        onChange={(e) => runSearch(1, Number(e.target.value))}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal isOpen={eventsModalOpen} toggle={closeEventsModal} className="modal-close">
        <ModalHeader toggle={closeEventsModal}>
          Historique de l&apos;archive supprimée
        </ModalHeader>
        <ModalBody>
          {eventsLoading ? (
            <div className="archive-loading">{SimpleSpinner()}</div>
          ) : eventsError ? (
            <div className="archive-empty">{eventsError}</div>
          ) : eventsList.length === 0 ? (
            <div className="archive-empty">Aucun événement enregistré.</div>
          ) : (
            <div className="archive-event-modal-list">
              {eventsList.map((event) => (
                <div
                  key={event.id || `${event.type}-${event.eventDate}`}
                  className="archive-event-modal-item"
                >
                  <div className="archive-event-modal">
                    <div className="archive-event-modal-header">
                      <span className="archive-event-modal-type">
                        {formatEventType(event.type)}
                      </span>
                      <span className="archive-event-modal-date">
                        {event.eventDate || '—'}
                      </span>
                    </div>
                    <ul className="archive-event-meta">
                      <li>
                        <span>Utilisateur</span>
                        <span>{formatEventAuthor(event)}</span>
                      </li>
                      {event.previousState && event.newState && (
                        <li>
                          <span>Statut</span>
                          <span>{`${event.previousState} → ${event.newState}`}</span>
                        </li>
                      )}
                      {event.comment && (
                        <li>
                          <span>Commentaire</span>
                          <span>{event.comment}</span>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          )}
          {eventsDocument && (
            <div className="archive-logs-doc mt-3">
              <div className="archive-logs-doc-title">{eventsDocument.title || '—'}</div>
              <div className="archive-logs-doc-meta">{eventsDocument.reference || '—'}</div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeEventsModal}>
            Fermer
          </Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default SearchArchiveItems;
